import React, { useContext, useEffect, useMemo, useState } from "react";
import Typography from "@material-ui/core/Typography";
import {
  Box,
  CircularProgress,
  FormControl,
  Hidden,
  Icon,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import { v4 as uuid } from "uuid";

import * as _ from "lodash";

import { useStyles, commonIcon } from "./HomeStyles";
import { useHistory } from "react-router-dom";
import { LayoutContext } from "../../../core/providers/LayoutContext";
import { AuthContext } from "../../../core/providers/AuthContext";
import {
  WORKSPACE_TAB,
  DASHBOARD_CARD_MEDIUM,
  DASHBOARD_CARD_LARGE,
  HOME_TAB,
  FORM_OPERATION_EXECUTION,
  BREAKPOINT_SM,
} from "../../../util/Constants";
import VersionTag from "../../../components/versionTag/VersionTag";
import { useTranslation } from "react-i18next";

import MicroseriveStatus from "../components/status/MicroserviceStatus";
import { DrawerContext } from "../../../core/providers/DrawerContext";
import { WorkspaceContext } from "../../../core/providers/WorkspaceContext";
import { useRequestHeaders } from "../../../core/hooks/useRequestHeaders";
import { useAccessesStore } from "../../../core/stores/AccessesStore";
import HomeCard from "../components/items/HomeCard";
import IndicatorCard from "../components/items/IndicatorCard";
import BandPositionalControls from "../components/controls/BandPositionalControls";
import FloatingActions from "../components/fab/FloatingActions";
import AddNewItemToBand from "../components/controls/AddNewItemToBand";
import { useInteractiveItemStore } from "../../../core/stores/InteractiveItemStore";
import useWindowDimensions from "../../../core/hooks/useWindowDimensions";
import FilterFieldsContainer from "../../../components/reportsContainer/fileds/FilterFieldsContainer";

const TEXT_HEADER_HOME = "dSuite Platform";

const SHOW_NEW_FILTER_VERSION = true;

export const MAP_SOL_IMAGES = {
  APO: { img: "logo-apollo-home.png", width: 25 },
  AVI: { img: "logo-avis-home.png", width: 25 },
  FEN: { img: "logo-fenix-home.png", width: 25 },
  FRI: { img: "logo-friday-home.png", width: 18 },
  MER: { img: "logo-mercurio-home.png", width: 20 },
  PMT: { img: "logo-oneprice-home.png", width: 20 },
  PLV: { img: "logo-plv-home.png", width: 26 },
  TIM: { img: "logo-timbo-home.png", width: 25 },
  BEA: { img: "logo-ems-home.png", width: 25 },
  DSU: { img: "logo-dsuite-home.png", width: 25 },
};

function Home() {
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();
  const { auth, logout } = useContext(AuthContext);
  const {
    changeTextHeader,
    changeCanGoBack,
    isDrawerOpen,
    changeActiveTab,
    activeTab,
  } = useContext(LayoutContext);
  const classes = useStyles({ isDrawerOpen });
  const { changeHeaderImg } = useContext(DrawerContext);
  const { addToFormHistory } = useContext(WorkspaceContext);
  const REQUEST_HEADERS = useRequestHeaders();
  const allLabel = t("RECORDS_PANEL_GROUPING_ALL");
  const wd = useWindowDimensions();
  const [loadOnDemandStates, setLoadOnDemandStates] = useState({});

  const {
    fetchingHomeItems,
    backgroundImg,
    dashboardSections,
    callServerToGetDashboard,
    selectedHomeSolution,
    changeSelectedHomeSolution,
    isEditing,
  } = useAccessesStore((state) => ({
    fetchingHomeItems: state.fetchingHomeItems,
    backgroundImg: state.backgroundImg,
    dashboardSections: state.dashboardSections,
    callServerToGetDashboard: state.callServerToGetDashboard,
    selectedHomeSolution: state.selectedHomeSolution,
    changeSelectedHomeSolution: state.changeSelectedHomeSolution,
    isEditing: state.isEditing,
  }));

  const {
    addTabItem,
    addSubItem,
    changeActiveTabItem,
    activeSubItem,
    changeActiveSubItem,
  } = useInteractiveItemStore();

  const handleChangeSolution = (e) => {
    changeSelectedHomeSolution({
      t,
      REQUEST_HEADERS,
      logout,
      newHomeSolution: e.target.value,
    });
  };

  const solutionsCombo = useMemo(() => {
    if (
      _.isNil(dashboardSections) ||
      _.isEmpty(dashboardSections) ||
      isEditing
    ) {
      return [];
    }

    const uniqueGroups = _.compact(
      Array.from(
        new Set(
          _.filter(dashboardSections, function (o) {
            const showDS =
              !_.isNil(o) && (_.isNil(o.hidden) || o.hidden === false);

            return showDS;
          }).map((a) => a?.group)
        )
      )
    );
    return uniqueGroups && uniqueGroups.length > 1 ? uniqueGroups : [];
  }, [dashboardSections, isEditing]);

  useEffect(() => {
    changeTextHeader(TEXT_HEADER_HOME);
    changeCanGoBack(false);
  }, [changeTextHeader, changeCanGoBack]);

  useEffect(() => {
    if (
      !_.isNil(auth) &&
      auth.isLogged === true &&
      (_.isNil(auth.mustChangePass) || auth.mustChangePass === false)
    ) {
      callServerToGetDashboard({ t, REQUEST_HEADERS });
    }

    return () => null;
    // eslint-disable-next-line
  }, [auth]);

  function handleCardClick(item, headerImg) {
    if (item) {
      const {
        propText,
        route,
        formPath,
        formName,
        icon,
        isExecution,
        defaultPinned,
      } = item;
      if (route && propText) {
        changeHeaderImg(headerImg);
        changeActiveTab(WORKSPACE_TAB);
        const routeToSet = isExecution ? `${route}/exe` : route;
        const paramsToSet = {
          route: routeToSet,
          path: formPath,
          idForm: formName,
          propText,
          name: icon,
          headerImg,
          isExecution,
          defaultPinned,
        };
        addToFormHistory(paramsToSet);
        addTabItem(paramsToSet);
        const uniqueIdForExe = uuid();
        const sub =
          isExecution === true
            ? {
                ...paramsToSet,
                parentId: paramsToSet.route,
                propText: formName,
                count_name: formName,
                count_number: uniqueIdForExe,
                entityName: `${formName}_${uniqueIdForExe}`,
                operation: FORM_OPERATION_EXECUTION,
                id: paramsToSet.route,
              }
            : {
                ...paramsToSet,
                parentId: paramsToSet.route,
                propText: t("MULTI_ITEM_SAVED_RECORDS"),
              };
        addSubItem(sub);
        changeActiveTabItem(paramsToSet.route);
        changeActiveSubItem(sub);
        if (activeSubItem.has(paramsToSet.route)) {
          const activeSubItemInMap = activeSubItem.get(paramsToSet.route);
          const routeToMove =
            activeSubItemInMap?.id || activeSubItemInMap?.route;
          history.push(routeToMove, activeSubItemInMap);
        } else {
          history.push(routeToSet, {
            path: formPath,
            idForm: formName,
            propText,
          });
        }
      }
    }
  }

  function resolveTitleComp(cardSize) {
    if (cardSize === DASHBOARD_CARD_LARGE) {
      return {
        c: "h4",
        v: "h4",
        is: 25,
        s: {
          marginLeft: 10,
          color: "#FFFFFF",
          fontSize: 36,
        },
      };
    } else if (cardSize === DASHBOARD_CARD_MEDIUM) {
      return {
        c: "h5",
        v: "h5",
        is: 20,
        s: {
          marginLeft: 10,
          color: "#FFFFFF",
          fontSize: 26,
        },
      };
    } else {
      //small or null
      return {
        c: "h5",
        v: "h5",
        is: 15,
        s: {
          marginLeft: 10,
          color: "#FFFFFF",
          fontSize: 22,
        },
      };
    }
  }

  function RefreshButton() {
    return (
      <Tooltip title={t("REFRESH_HOME")} placement="left">
        <IconButton
          style={{ marginLeft: 5, alignSelf: "flex-end" }}
          onClick={() =>
            callServerToGetDashboard({ t, REQUEST_HEADERS, logout })
          }
        >
          <Icon
            className="fas fa-sync"
            style={{ ...commonIcon, fontSize: 20, color: "#FFFFFF" }}
          />
        </IconButton>
      </Tooltip>
    );
  }

  const backgroundImageCalculated = useMemo(() => {
    if (!_.isNil(backgroundImg) && !_.includes(backgroundImg, "https://")) {
      if (_.includes(backgroundImg, "assets")) {
        return `url(${backgroundImg})`;
      }

      return `url("/assets/img/home-bg/${backgroundImg}")`;
    }

    return `url("/assets/img/home-bg/default-bg.png")`;
  }, [backgroundImg]);

  const handleLoadOnDemand = (sectionIndex) => {
    setLoadOnDemandStates((prev) => ({
      ...prev,
      [sectionIndex]: true,
    }));
  };

  if (fetchingHomeItems) {
    return (
      <div
        style={{
          backgroundColor: theme.palette.content.dashboardFetchingBackground,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        className={classes.rootContainer}
      >
        <CircularProgress disableShrink />
      </div>
    );
  }

  return (
    <div
      className={classes.rootContainer}
      style={{
        backgroundColor:
          !backgroundImg &&
          !fetchingHomeItems &&
          theme.palette.content.dashboardFetchingBackground,
        backgroundImage: backgroundImageCalculated,
      }}
    >
      <div className={classes.opaqueBackground}>
        <>
          <div className={classes.homeFilters}>
            {auth &&
              !_.isNil(auth.companies) &&
              !_.isEmpty(auth.companies) &&
              _.some(auth.companies, { codCompany: "DOITS" }) &&
              activeTab !== WORKSPACE_TAB && (
                <MicroseriveStatus company={auth.company.codCompany} />
              )}
            {solutionsCombo.length > 0 && (
              <div className={classes.solutionsComboContainer}>
                {SHOW_NEW_FILTER_VERSION && wd.width > BREAKPOINT_SM ? (
                  <img
                    alt="solution-filter-logo"
                    src={`${process.env.PUBLIC_URL}/assets/icons/home/solution.svg`}
                    className={classes.solutionComboImage}
                  />
                ) : (
                  <Icon
                    className="fas fa-filter"
                    style={{ ...commonIcon, fontSize: 22, color: "#FFF" }}
                  />
                )}

                <Typography
                  variant="h5"
                  className={classes.solutionsComboLabel}
                >
                  {t("SOLUTIONS_COMBO_LABEL")}
                </Typography>

                {SHOW_NEW_FILTER_VERSION && wd.width > BREAKPOINT_SM ? (
                  <div className={classes.newSolutionsFilterContainer}>
                    <button
                      value={""}
                      className={
                        _.isNil(selectedHomeSolution) ||
                        selectedHomeSolution === ""
                          ? classes.newSolutionFilterSelected
                          : classes.newSolutionFilter
                      }
                      onClick={() =>
                        handleChangeSolution({ target: { value: "" } })
                      }
                    >
                      <Typography variant="h6">
                        {t("GEN_DASH_FILTER_ALL")}
                      </Typography>
                    </button>
                    {solutionsCombo
                      .filter((x) => !_.isNil(x) && x !== "" && x !== "null")
                      .sort((a, b) => a.localeCompare(b))
                      .map((s, index) => {
                        const idMap = _.trim(s).substring(0, 3).toUpperCase();
                        return (
                          <button
                            key={index}
                            value={s}
                            className={
                              selectedHomeSolution === s
                                ? classes.newSolutionFilterSelected
                                : classes.newSolutionFilter
                            }
                            onClick={() =>
                              handleChangeSolution({ target: { value: s } })
                            }
                          >
                            {_.has(MAP_SOL_IMAGES, idMap) && (
                              <img
                                alt={`${s}-filter-logo`}
                                src={`${process.env.PUBLIC_URL}/assets/icons/home/${MAP_SOL_IMAGES[idMap]?.img}`}
                                style={{ width: MAP_SOL_IMAGES[idMap]?.width }}
                              />
                            )}
                            <Typography variant="h6">{s}</Typography>
                          </button>
                        );
                      })}
                  </div>
                ) : (
                  <FormControl className={classes.formControl}>
                    <Select
                      value={selectedHomeSolution}
                      onChange={handleChangeSolution}
                      id="solution_combo"
                      className={classes.comboSolution}
                      disableUnderline
                      inputProps={{
                        classes: {
                          icon: classes.comboSolutionIcon,
                          select: classes.comboSolutionSelect,
                        },
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" style={{ paddingRight: 12 }}>
                        <Typography>{allLabel}</Typography>
                      </MenuItem>
                      {solutionsCombo
                        .filter((x) => !_.isNil(x) && x !== "" && x !== "null")
                        .map((s, index) => {
                          return (
                            <MenuItem
                              key={index}
                              value={s}
                              style={{ paddingRight: 12 }}
                            >
                              {s}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                )}
              </div>
            )}
            {activeTab === HOME_TAB && (
              <Hidden mdUp>
                <RefreshButton />
              </Hidden>
            )}
          </div>

          {dashboardSections &&
            dashboardSections.length > 0 &&
            activeTab !== WORKSPACE_TAB &&
            dashboardSections
              .filter((ds) => {
                if (
                  !_.isNil(ds) &&
                  !_.isNil(ds.hidden) &&
                  ds.hidden === true &&
                  !isEditing
                ) {
                  return false;
                }

                if (
                  _.isNil(selectedHomeSolution) ||
                  selectedHomeSolution === "" ||
                  selectedHomeSolution === allLabel
                ) {
                  return true;
                }

                return ds?.group === selectedHomeSolution;
              })
              .map((ds, sectionIndex) => {
                const {
                  titleText,
                  titleIcon,
                  cardSize,
                  items,
                  headerImg,
                  hidden,
                  filters,
                  loadOnDemand,
                } = ds;

                const titleComponent = resolveTitleComp(cardSize);
                const isImageIcon =
                  !_.isNil(titleIcon) &&
                  _.isString(titleIcon) &&
                  _.endsWith(_.toLower(titleIcon), ".png");

                const imageSrc = `${process.env.PUBLIC_URL}/assets/icons/home/${titleIcon}`;
                const isHidden = hidden === true;

                const hasFilters =
                  !_.isNil(filters) &&
                  _.isArray(filters) &&
                  !_.isEmpty(filters);

                const loadOnRender =
                  loadOnDemand === false ||
                  loadOnDemandStates[sectionIndex] ||
                  _.isNil(loadOnDemand);

                return (
                  <div key={`${titleText}_${sectionIndex}`}>
                    {/* ICON + TITLE */}
                    <div
                      className={classes.titleContainer}
                      style={{
                        marginTop: sectionIndex > 0 ? 30 : 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "#FFFFFF",
                          backgroundColor: isHidden
                            ? "grey"
                            : theme.palette.content.mainColor,
                          width: `${titleComponent?.is * 2}px`,
                          height: `${titleComponent?.is * 2}px`,
                          borderRadius: `${titleComponent?.is * 2}px`,
                        }}
                      >
                        {isImageIcon ? (
                          <img
                            src={imageSrc}
                            alt={titleIcon}
                            className={
                              _.includes(_.toLower(titleIcon), "friday")
                                ? classes.imgIconHome42
                                : classes.imgIconHome
                            }
                          />
                        ) : (
                          <Icon
                            className={
                              titleIcon ? titleIcon : "fas fa-list-alt"
                            }
                            style={{
                              ...commonIcon,
                              fontSize: titleComponent?.is,
                            }}
                          />
                        )}
                      </div>

                      {titleText && (
                        <Typography
                          variant={titleComponent?.v}
                          component={titleComponent?.c}
                          style={titleComponent?.s}
                        >
                          {titleText}
                        </Typography>
                      )}

                      {loadOnDemand === true &&
                        !loadOnDemandStates[sectionIndex] && (
                          <IconButton
                            className={classes.loadnOnDemandBtn}
                            onClick={() => handleLoadOnDemand(sectionIndex)}
                          >
                            <Icon className="fas fa-eye" style={commonIcon} />
                          </IconButton>
                        )}
                    </div>

                    {hasFilters ? (
                      <Box mb={2}>
                        <FilterFieldsContainer availableFilters={filters} />
                      </Box>
                    ) : null}

                    {/* GRID OF ITEMS */}
                    {items && items.length > 0 && loadOnRender && (
                      <Grid
                        container
                        spacing={2}
                        key="grid-container"
                        className={isEditing ? classes.gridBandEditing : ""}
                      >
                        {isEditing && (
                          <BandPositionalControls
                            ds={ds}
                            hidden={ds?.hidden}
                            sectionIndex={sectionIndex}
                            sectionsLength={
                              dashboardSections.filter((ds) => {
                                if (
                                  !_.isNil(ds) &&
                                  !_.isNil(ds.hidden) &&
                                  ds.hidden === true &&
                                  !isEditing
                                ) {
                                  return false;
                                }

                                if (
                                  _.isNil(selectedHomeSolution) ||
                                  selectedHomeSolution === "" ||
                                  selectedHomeSolution === allLabel
                                ) {
                                  return true;
                                }

                                return ds?.group === selectedHomeSolution;
                              }).length
                            }
                          />
                        )}
                        {items
                          .filter((i) => !i?.hidden || isEditing)
                          .map((item, cardIndex) => {
                            const { isKpi } = item;

                            if (!_.isNil(isKpi) && isKpi) {
                              return (
                                <IndicatorCard
                                  key={cardIndex}
                                  kpiName={item?.route}
                                  item={item}
                                  index={cardIndex}
                                  cardSize={cardSize}
                                  sectionIndex={sectionIndex}
                                  sectionHidden={ds?.hidden}
                                  itemsLength={items?.length || 0}
                                  loadOnRender={loadOnRender}
                                />
                              );
                            } else {
                              return (
                                <HomeCard
                                  key={cardIndex}
                                  item={item}
                                  index={cardIndex}
                                  handleCardClick={handleCardClick}
                                  headerImg={headerImg}
                                  cardSize={cardSize}
                                  sectionIndex={sectionIndex}
                                  sectionHidden={ds?.hidden}
                                  itemsLength={items?.length || 0}
                                />
                              );
                            }
                          })}

                        {isEditing && (
                          <AddNewItemToBand
                            sectionIndex={sectionIndex}
                            sectionHidden={ds?.hidden}
                            bandName={titleText}
                          />
                        )}
                      </Grid>
                    )}
                  </div>
                );
              })}
        </>
        <Hidden smDown>
          <FloatingActions />
        </Hidden>
        <VersionTag />
      </div>
    </div>
  );
}

Home.whyDidYouRender = true;

export default Home;
